import React from "react";
import PropTypes from "prop-types";
import { reportFrontendError } from "./frontendErrorReport";
import { chunkErrorContext } from './chunkDiagnostics';
import ErrorFallback from "component/ErrorFallback";
import { recoverChunkLoad, isChunkLoadError } from "./chunkRecovery";

const createErrorReference = () =>
  `EB-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`.toUpperCase();

class ErrorBoundary extends React.Component {
  state = {
    hasError: false,
    errorMessage: "",
    errorReference: "",
  };

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      chunkFailure: isChunkLoadError(error),
      errorMessage: error && error.message ? error.message : String(error),
      errorReference: createErrorReference(),
    };
  }

  componentDidCatch(error, info) {
    // The raw message stays in the logs; the UI only shows the reference.
    this.logErrorToServices(
      `[ErrorBoundary ${this.state.errorReference}] ${this.state.errorMessage}`,
      info.componentStack,
    );
    const report = (decision) => reportFrontendError({ reference: this.state.errorReference, error,
      componentStack: info.componentStack, chunk: decision ? chunkErrorContext(error, decision) : undefined });
    if (isChunkLoadError(error)) {
      recoverChunkLoad(error, { reference: this.state.errorReference, onDecision: report });
    } else {
      report();
      recoverChunkLoad(error);
    }
  }

  // Keep the full exception local; the remote report contains only safe diagnostics.
  logErrorToServices = console.log;

  handleRetry = () => {
    // React.lazy caches a rejected import. Resetting only this boundary retries
    // the same rejection and cannot load the assets from a newer deployment.
    if (this.state.chunkFailure) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, errorMessage: "", errorReference: "" });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback reference={this.state.errorReference} chunkFailure={this.state.chunkFailure} onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = { children: PropTypes.node };
export default ErrorBoundary;
